// Signed in, but the account has no Baby Plan access (status "no-access" in lib/session.tsx): explain it plainly
// and give a way out. Before this screen the app signed such people out and they bounced between /login and the app.
// Owner's rule: nothing here about cancelling or managing a subscription.
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { track } from '@/lib/analytics';
import { useSession } from '@/lib/session';
import { supportMailto, trackEmailClick } from '@/features/help/support';
import { SkyBackdrop } from '@/themes/glass/Sky';
import { useSkin } from '@/lib/skin';
import '@/features/help/help.css';

export function NoAccess() {
  const { t } = useTranslation('help');
  const s = useSession();
  const glass = useSkin() === 'glass';
  const [phase, setPhase] = useState<'idle' | 'checking' | 'still'>('idle');

  useEffect(() => { track('paywall_view', { source: 'app', surface: 'no_access' }, 'paywall'); }, []);

  const check = async () => {
    setPhase('checking');
    await s.refresh();
    setPhase('still'); // if access is back, this screen is already gone
  };

  return (
    <div className="screen">
      {glass && <SkyBackdrop />}
      <main className="page help-page no-access">
        <h1 className="lg-title">{t('access.title')}</h1>
        <p className="hint">{t('access.lead', { email: s.me?.email ?? '' })}</p>
        <p className="hint">{t('access.justBought')}</p>
        <p className="hint">{t('access.invited')}</p>
        {(s.me?.booksOwned?.length ?? 0) > 0 && (
          <section className="tile set-card no-access-book">
            <p><b>{t('access.bookTitle')}</b></p>
            <p className="hint">{t('access.bookLead')}</p>
            {s.me!.booksOwned!.map((id) => (s.me!.booksReady?.includes(id)
              ? <a key={id} className="btn btn-quiet btn-block" href={`/api/books/${id}/download`}>{t('access.bookDownload')} · {t(`access.books.${id}`)}</a>
              : <p key={id} className="hint">{t(`access.books.${id}`)}: {t('access.bookPreparing')}</p>))}
          </section>
        )}
        <button className="btn btn-primary btn-block" type="button" disabled={phase === 'checking'} onClick={() => void check()}>
          {phase === 'checking' ? t('access.checking') : t('access.check')}
        </button>
        {phase === 'still' && <p className="hint" role="status">{t('access.stillNo')}</p>}
        <a className="btn btn-quiet btn-block" href={supportMailto({ email: s.me?.email })} onClick={() => trackEmailClick('no_access')}>{t('sheet.email')}</a>
        <Link className="btn btn-text btn-block" to="/help">{t('access.help')}</Link>
        <button className="btn btn-text btn-block" type="button" onClick={() => void s.logout()}>{t('access.other')}</button>
      </main>
    </div>
  );
}
